import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { CronJobPayload, PartialCronJobPayload } from './types';

import { CRON_JOBS } from 'RestAPIs/endpoints';
import { useCreateResource } from 'RestAPIs/useCreateResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

const cronSchema = Yup.object().shape({ cron: Yup.string() });

const useTheme = makeStyles(() => ({
  formContent: {
    marginTop: 20,
    display: 'flex',
    gap: 20,
    flexDirection: 'column'
  }
}));

interface Props {
  onClose: () => void;
  schema: Yup.AnyObjectSchema;
  convertData?: (data: CronJobPayload) => CronJobPayload;
  defaultValues?: PartialCronJobPayload;
}

export const CronJobsCRUDForm: React.FC<Props> = ({
  onClose,
  children,
  schema,
  convertData,
  defaultValues
}) => {
  const { t } = useTranslation();
  const classes = useTheme();
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        ...cronSchema.fields,
        ...schema.fields
      })
    ),
    defaultValues: schema.cast(defaultValues ?? {})
  });

  const { mutateAsync: handleCreate, isLoading } =
    useCreateResource<CronJobPayload>({
      url: CRON_JOBS,
      invalidateQueryKey: ['cron-jobs', 'list']
    });

  const submitForm = (data: CronJobPayload) => {
    handleCreate(convertData ? convertData(data) : data).then(onClose);
  };

  return (
    <MultiStepForm
      methods={methods}
      onSubmit={methods.handleSubmit(submitForm)}
    >
      <div className={classes.formContent}>
        {children}
        <ControlledTextField
          name="cron"
          control={methods.control}
          variant="outlined"
          label={t('cronJobs.form.cronSchema')}
        />
        <span>
          {t('cronJobs.form.formatExample')}
          <a
            href={'http://www.cronmaker.com/'}
            target={'_blank'}
            rel="noreferrer"
          >
            {t('cronJobs.form.readMoreHere')}
          </a>
        </span>
      </div>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
